import React, { useCallback, useEffect, useState } from "react";
import { MuiCard } from "@ace/ace-ui-web";
import { CircularProgress, LinearProgress, Typography, Stack, Grid } from '@mui/material';
import { CalendarMonth, Cancel, CheckCircle, EventBusy, Layers, Percent, SignalCellularAlt, Timer } from "@mui/icons-material";
import StatCard from "../../components/statcard";
import styles from "./kpiReport.module.css";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchShiftKpi } from '../../redux/KpiReducer/kpiSlice';
import { getDefaultReportStartDate, getDefaultReportEndDate } from '../../utils/reportDateDefaults';

const KPIReportPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user: currentUser } = useAppSelector((state) => state.currentUser);
    const { kpiData, loading, error } = useAppSelector((state) => state.kpi);

    const [filter, setFilter] = useState({
        fromDate: getDefaultReportStartDate(),
        toDate: getDefaultReportEndDate(),
    });

    const dispatchFetchKpi = useCallback((fromDate: string, toDate: string) => {
        if (!currentUser?.siteCd) return;
        dispatch(fetchShiftKpi({
            siteCd: currentUser.siteCd,
            startDate: fromDate || undefined,
            endDate: toDate || undefined,
        }));
    }, [dispatch, currentUser]);

    // Fetch KPI data on page load with default dates.
    // dispatchFetchKpi is stable until currentUser changes, so this
    // only re-runs when the user becomes available — not on date changes.
    useEffect(() => {
        dispatchFetchKpi(filter.fromDate, filter.toDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatchFetchKpi]);

    // Show error toast when API call fails
    useEffect(() => {
        if (error) {
            toast.error(`Failed to load KPI data: ${error}`);
        }
    }, [error]);

    const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setFilter(prev => ({ ...prev, fromDate: newDate }));
        dispatchFetchKpi(newDate, filter.toDate);
    };

    const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setFilter(prev => ({ ...prev, toDate: newDate }));
        dispatchFetchKpi(filter.fromDate, newDate);
    };

    // Helper function to safely calculate percentages
    const calculatePercentage = (value: number, total: number): number => {
        if (!total || total === 0) return 0;
        return +(value / total * 100).toFixed(1);
    }

    // Map API response to local accessors for cleaner template usage
    const statistics = {
        totalShifts: kpiData?.totalPostedShifts || 0,
        notGrabbedShifts: kpiData?.notGrabbedShifts || 0,
        approvedShifts: kpiData?.approvedShifts || 0,
        rejectedShifts: kpiData?.rejectedShifts || 0,
        cancelledShifts: kpiData?.canceledShifts || 0,
        grabbedShifts: kpiData?.grabbedShifts || 0,
        openShifts: kpiData?.openShifts || 0,
        vtoShifts: kpiData?.vtoShifts || 0,
        swapShifts: kpiData?.swapShifts || 0,
        openPercentage: Number.parseFloat(kpiData?.openPercentage || "0"),
        vtoPercentage: Number.parseFloat(kpiData?.vtoPercentage || "0"),
        swapPercentage: Number.parseFloat(kpiData?.swapPercentage || "0"),
    };

    return (
        <Stack spacing={2.5} sx={{ width: '100%', p: { xs: 1, sm: 2 } }}>
            {/* Filter Card */}
            <MuiCard
                borderRadius={2}
                elevation={5}
                padding={0}
                variant="elevation"
                sx={{ width: '100%' }}
            >
                <Stack spacing={2} sx={{ p: 2 }}>
                    <Typography variant="h6">
                        Date Range Filter
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={0.5} className={[styles.formGroup, 'form-group'].join(" ")}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <CalendarMonth sx={{ fontSize: 15, color: "gray" }} />
                                    <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', fontWeight: 500, display: 'flex', alignItems: 'center' }}>Start Date</Typography>
                                </Stack>
                                <input
                                    id="fromDate"
                                    type="date"
                                    value={filter.fromDate}
                                    max={filter.toDate}
                                    onChange={handleFromDateChange}
                                    style={{ width: '100%' }}
                                />
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={0.5} className={[styles.formGroup, 'form-group'].join(" ")}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <CalendarMonth sx={{ fontSize: 15, color: "gray" }} />
                                    <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', fontWeight: 500, display: 'flex', alignItems: 'center' }}>End Date</Typography>
                                </Stack>
                                <input
                                    id="toDate"
                                    type="date"
                                    value={filter.toDate}
                                    min={filter.fromDate}
                                    onChange={handleToDateChange}
                                    style={{ width: '100%' }}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
            </MuiCard>

            {/* Data Section — loading spinner rendered inline so the filter card stays mounted */}
            {loading ? (
                <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: '300px' }}>
                    <CircularProgress />
                    <Typography variant="body1">Loading KPI data...</Typography>
                </Stack>
            ) : (
                <>
                    {/* Shift Status Summary */}
                    <Stack spacing={1.5}>
                        <Typography variant="h6" sx={{ pt: 1.5 }}>
                            Shift Status Summary
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                                <StatCard
                                    value={statistics.totalShifts}
                                    title="Total Shifts Created"
                                    note="Does not include rejected shifts"
                                    IconProp={<Layers />}
                                    colorProp="lightblue"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                                <StatCard
                                    value={statistics.notGrabbedShifts}
                                    title="Posted Shifts"
                                    note="Posted status, not yet grabbed"
                                    IconProp={<Timer />}
                                    colorProp="yellow"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                                <StatCard
                                    value={statistics.approvedShifts}
                                    title="Approved Shifts"
                                    note="Shifts in approved status"
                                    IconProp={<CheckCircle />}
                                    colorProp="green"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                                <StatCard
                                    value={statistics.rejectedShifts}
                                    title="Rejected Claims"
                                    note="Shift claims that were denied"
                                    IconProp={<Cancel />}
                                    colorProp="red"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                                <StatCard
                                    value={statistics.cancelledShifts}
                                    title="Cancelled Shifts"
                                    note="Shift posts that expired without being grabbed"
                                    IconProp={<EventBusy />}
                                    colorProp="purple"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                                <StatCard
                                    value={calculatePercentage(statistics.approvedShifts, statistics.totalShifts)}
                                    isPercentage={true}
                                    title="Percentage of Shifts Grabbed and Approved"
                                    note={`${statistics.approvedShifts} of ${statistics.totalShifts}`}
                                    IconProp={<Percent />}
                                    colorProp="blue"
                                />
                            </Grid>
                        </Grid>
                    </Stack>

                    {/* Status Breakdown Card */}
                    <MuiCard
                        borderRadius={2}
                        elevation={5}
                        padding={0}
                        variant="outlined"
                        sx={{ width: '100%', backgroundColor: '#f7f7f7' }}
                    >
                        <Stack spacing={2} sx={{ p: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mt: 0.5 }}>
                                Status Breakdown
                            </Typography>
                            <Stack spacing={2}>
                                <Stack spacing={0.5}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="subtitle2">Approved</Typography>
                                        <Typography variant="subtitle2" sx={{ color: '#2e7d32' }}>
                                            {statistics.approvedShifts}
                                        </Typography>
                                    </Stack>
                                    <LinearProgress
                                        color="success"
                                        variant="determinate"
                                        value={calculatePercentage(statistics.approvedShifts, statistics.totalShifts)}
                                        sx={{ height: 10, borderRadius: 5 }}
                                    />
                                </Stack>
                                <Stack spacing={0.5}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="subtitle2">Rejected</Typography>
                                        <Typography variant="subtitle2" sx={{ color: '#d32f2f' }}>
                                            {statistics.rejectedShifts}
                                        </Typography>
                                    </Stack>
                                    <LinearProgress
                                        color="error"
                                        variant="determinate"
                                        value={calculatePercentage(statistics.rejectedShifts, statistics.totalShifts)}
                                        sx={{ height: 10, borderRadius: 5 }}
                                    />
                                </Stack>
                                <Stack spacing={0.5}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="subtitle2">Cancelled</Typography>
                                        <Typography variant="subtitle2" sx={{ color: '#0288d1' }}>
                                            {statistics.cancelledShifts}
                                        </Typography>
                                    </Stack>
                                    <LinearProgress
                                        color="info"
                                        variant="determinate"
                                        value={calculatePercentage(statistics.cancelledShifts, statistics.totalShifts)}
                                        sx={{ height: 10, borderRadius: 5 }}
                                    />
                                </Stack>
                            </Stack>
                        </Stack>
                    </MuiCard>

                    {/* Shift Type Distribution */}
                    <Stack spacing={1.5}>
                        <Typography variant="h6" sx={{ pt: 1.5 }}>
                            Shift Type Distribution
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <StatCard
                                    value={statistics.openShifts}
                                    title="Shift"
                                    note=""
                                    IconProp={<SignalCellularAlt />}
                                    colorProp="red"
                                    variant="shifttype"
                                    percentage={calculatePercentage(statistics.openShifts, statistics.totalShifts)}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <StatCard
                                    value={statistics.vtoShifts}
                                    title="VTO"
                                    note=""
                                    IconProp={<SignalCellularAlt />}
                                    colorProp="black"
                                    variant="shifttype"
                                    percentage={calculatePercentage(statistics.vtoShifts, statistics.totalShifts)}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <StatCard
                                    value={statistics.swapShifts}
                                    title="Swap"
                                    note=""
                                    IconProp={<SignalCellularAlt />}
                                    colorProp="yellow"
                                    variant="shifttype"
                                    percentage={calculatePercentage(statistics.swapShifts, statistics.totalShifts)}
                                />
                            </Grid>
                        </Grid>
                    </Stack>

                    {/* Fill Rate Summary */}
                    <Stack spacing={1.5}>
                        <Typography variant="h6" sx={{ pt: 1.5 }}>
                            Fill Rate Summary
                        </Typography>
                        <StatCard
                            value={statistics.grabbedShifts}
                            title="Shifts Grabbed but Waiting on Approval"
                            colorProp="green"
                            variant="fillrate"
                            percentage={calculatePercentage(statistics.grabbedShifts, statistics.totalShifts)}
                        />
                    </Stack>
                </>
            )}
        </Stack>
    );
};

export default KPIReportPage;